import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, CardHeader, Collapse, FormFeedback, FormGroup, Input, Label } from "reactstrap";
import { CoreDnpExportConfiguration, CoreDnpExportSchoolUnit, DnpEnvironment, DnpOrganization, DnpOrganisationStatus } from "../../../../../../models/CoreDnpModels";
import { CoreConfigurationSchoolUnit } from "../../../../../../models/CoreSchoolUnit";
import { ClearDnpOrganization, downloadFileForMoa, GetDnpOrganization } from "../../../../../../store/DnpAction";
import EnumInput from "../../../../../misc/EnumInput";
import Loading from "../../../../../misc/Loading";
import DnpSchoolUnits from "./DnpSchoolUnits";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BoolInput from "../../../../../misc/BoolInput";
import { useTranslation } from 'react-i18next';
import { RequestState } from "../../../../../../models/RequestState";
import StatusCode from "../../../../../../util/StatusCode";
import { CoreExportModuleSchoolUnit } from "../../../../../../models/CoreModels";
import { RootState } from "../../../../../../store/store";


export const warningMessages = (configuration: CoreDnpExportConfiguration, updates: Partial<CoreDnpExportConfiguration>) => {
    const warnings: string[] = [];

    if (updates.dnpSchoolUnits) {
        configuration.dnpSchoolUnits.forEach(config => {
            if (updates.dnpSchoolUnits &&
                !updates.dnpSchoolUnits.some(update => update.dnpSchoolUnitId === config.dnpSchoolUnitId)) {
                warnings.push("Om du tar bort skolan så kommer skolan samt aktiviteter, grupper, elever och tjänstgöringar kopplade till skolan raderas")
            }
        })
    }
    return warnings;
};

export const validator = (configuration: CoreDnpExportConfiguration, schoolUnits: CoreExportModuleSchoolUnit[], dnpOrganization: RequestState<DnpOrganization>) => {
    const validation: string[] = [];

    //Wrong org num
    if (!configuration.organizationNumber || configuration.organizationNumber.length !== 10) {
        validation.push('organizationNumber')
    }

    //Org not found
    if (dnpOrganization.code === StatusCode.ERROR && dnpOrganization.error.request.status === 404) {
        validation.push('organizationNotFound')
    }

    //Mapped schoolUnits which cannot be found at skolverket
    if (dnpOrganization.code === StatusCode.COMPLETE) {
        configuration.dnpSchoolUnits.forEach(dsu => {
            if (!dnpOrganization.data?.schoolUnits?.find(s => s.status === DnpOrganisationStatus.ACTIVE && s.id === dsu.dnpSchoolUnitId)) {
                validation.push(`schoolUnitMissingFromSkolverket-${dsu.dnpSchoolUnitId}`)
            }
        })
    }

    //No active flowsync schoolunit found
    configuration.dnpSchoolUnits.forEach(x => {
        if (x.mappedSchoolUnitIds === undefined || x.mappedSchoolUnitIds.length === 0) {
            validation.push(`mappedSchoolUnitIds-${x.dnpSchoolUnitId}`)
            return;
        }
        if (x.mappedSchoolUnitIds.some(schoolUnitId => !schoolUnits.filter(su => su.isActive).map(su => su.id).includes(schoolUnitId))) {
            validation.push(`inactiveSchoolUnit-${x.dnpSchoolUnitId}`)
        }
    });

    return validation;
}


interface DnpExportConfigurationProps {
    moduleId?: string
    configuration: CoreDnpExportConfiguration
    originalConfiguration: CoreDnpExportConfiguration
    schoolUnits: CoreExportModuleSchoolUnit[]
    disabled: boolean
    updatePart: (part: Partial<CoreDnpExportConfiguration>) => void
    onValidChange: (arg: boolean) => void 
}

const environmentOptions = [
    DnpEnvironment.TEST,
    DnpEnvironment.PROD
]

const DnpExportConfiguration: React.FC<DnpExportConfigurationProps> = (props) => {
    const { configuration } = props;
    const { organizationNumber, environment } = configuration;
    const dispatch = useDispatch();
    const { t } = useTranslation(['configuration', 'common'])

    const dnpOrganization = useSelector<RootState, RequestState<DnpOrganization>>(state => state.dnp.dnpOrganization);

    const [downloadingMetadata, setDownloadingMetadata] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.schoolUnits, dnpOrganization);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration]);

    useEffect(() => {
        if (validation.indexOf('organizationNumber') === -1) {
            if (organizationNumber) {
                GetDnpOrganization(environment, organizationNumber)(dispatch);
            }
        } else {
            props.updatePart({ organizationName: undefined });
            ClearDnpOrganization()(dispatch);
        }
    }, [dispatch, organizationNumber, environment]);

    const downloadFile = async () => {
        if (downloadingMetadata) {
            return;
        }

        if (props.moduleId) {
            setDownloadingMetadata(true);
            await downloadFileForMoa(props.moduleId)();
            setDownloadingMetadata(false);
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                <span>
                    {t('modules.DNPExport.title', { ns: 'common' })}
                    <a href="https://flowsync.se/help/modules/export/dnp#konfiguration-digitala-nationella-prov" target="_blank">
                        <FontAwesomeIcon className="documentation-icon" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a>
                </span>
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>

                    <EnumInput
                        title={t('dnpExportForm.environment.title', { ns: 'configuration' })}
                        value={configuration.environment}
                        options={environmentOptions.map(key => ({
                            value: key,
                            text: t(`dnpExportForm.environment.options.${key}`, { ns: 'configuration' })
                        }))}
                        onChange={(name, value) => props.updatePart({ environment: value })}
                        disabled={props.disabled}
                        name='environment'
                        invalid={validation.indexOf('environment') !== -1}
                        invalidFeedback={t('dnpExportForm.environment.invalidFeedback', { ns: 'configuration' })}
                    />

                    <FormGroup>
                        <Label tag="h5" for="organizationNumber">{t('dnpExportForm.organizationNumber.label', { ns: 'configuration' })} {configuration.organizationName && <>({configuration.organizationName})</>}</Label>
                        <Input
                            type="text"
                            name="organizationNumber"
                            id="organizationNumber"
                            placeholder={t('dnpExportForm.organizationNumber.label', { ns: 'configuration' })}
                            value={configuration.organizationNumber || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ organizationNumber: e.target.value })}
                            invalid={validation.indexOf('organizationNumber') !== -1 || validation.indexOf('organizationNotFound') !== -1}
                            autoComplete="off" />
                        {validation.indexOf('organizationNumber') !== -1 && <FormFeedback>{t('dnpExportForm.organizationNumber.formFeedbackOrganizationNumber', { ns: 'configuration' })}</FormFeedback>}
                        {validation.indexOf('organizationNotFound') !== -1 && <FormFeedback>{t('dnpExportForm.organizationNumber.formFeedbackNotFound', { ns: 'configuration' })}</FormFeedback>}
                    </FormGroup>

                    <BoolInput
                        title={t('dnpExportForm.includeAllDuties.title', { ns: 'configuration' })}
                        value={configuration.includeAllDuties}
                        updateValue={(name, value) => props.updatePart({ includeAllDuties: value })}
                        disabled={props.disabled}
                        name='includeAllDuties'
                    />

                    <DnpSchoolUnits
                        savedDnpSchoolUnits={configuration.dnpSchoolUnits}
                        mergedDnpSchoolUnits={configuration.dnpSchoolUnits}
                        schoolUnits={props.schoolUnits}
                        disabled={props.disabled}
                        updatePropertyValue={(name, value) => props.updatePart({ [name]: value })}
                        orgNum={configuration.organizationNumber}
                        environment={configuration.environment}
                        validation={validation}
                        dnpExportId={configuration.id}
                    />

                    <FormGroup className="mt-4">
                        <h5>{t('dnpExportForm.metaData.title', { ns: 'configuration' })}<a href="https://flowsync.se/help/modules/export/dnp#metadata-for-moa" target="_blank"><FontAwesomeIcon className="documentation-icon fs-color" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a></h5>
                        <p>{t('dnpExportForm.metaData.description1', { ns: 'configuration' })}</p>
                        {!configuration.organizationNumber &&
                            <p className="blue"><i>{t('dnpExportForm.metaData.description2', { ns: 'configuration' })}</i></p>}
                        {props.originalConfiguration.organizationNumber &&
                            (props.configuration.organizationNumber || props.configuration.organizationName) &&
                            validation.indexOf('organizationNumber') === -1 &&
                            validation.indexOf('organizationNotFound') === -1 &&
                            <p className="blue"><i>{t('dnpExportForm.metaData.description3', { ns: 'configuration' })}</i></p>}
                        <Button
                            title={t('dnpExportForm.metaData.download', { ns: 'configuration' })}
                            disabled={props.disabled || !props.configuration.organizationNumber}
                            onClick={downloadFile}>
                            {t('dnpExportForm.metaData.download', { ns: 'configuration' })}
                        </Button>
                        {downloadingMetadata && <Loading style={{ display: 'inline-block', marginLeft: '10px' }} />}
                    </FormGroup>
                </CardBody>
            </Collapse>
        </Card>
    );
}

export default DnpExportConfiguration;
